import { useBetaStatus } from "@/hooks/useBetaStatus";
import {
  useQuestionList,
  UseQuestionListParams,
  type UseQuestionListResult,
} from "@/hooks/useQuestionList";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface UserIntentTableProps {
  /**
   * 用户意图问题列表（如果提供，将使用静态数据）
   */
  questions?: string[];
  /**
   * 动态数据请求参数（如果提供，将使用动态数据）
   */
  questionParams?: UseQuestionListParams;
  /**
   * 外部传入的数据源（若提供，将优先使用，组件内部不再发起请求）
   */
  dataSource?: UseQuestionListResult;
  /**
   * 下载按钮点击事件
   */
  onDownload?: () => void;
  /**
   * 问题行点击事件
   */
  onQuestionClick?: (question: string, index: number) => void;
  /**
   * 表格标题，默认为t('common.charts.intent_title')
   */
  title?: string;
  /**
   * 下载按钮文本，默认为t('common.charts.intent_btn_more')
   */
  downloadButtonText?: string;
  /**
   * 是否显示下载按钮，默认为true
   */
  showDownloadButton?: boolean;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * 表格容器自定义样式
   */
  containerStyle?: React.CSSProperties;
}

// 默认用户意图问题列表（已移除，完全依赖 API 数据）

const UserIntentTable: React.FC<UserIntentTableProps> = ({
  questions,
  questionParams,
  dataSource,
  onDownload,
  onQuestionClick,
  title,
  downloadButtonText,
  showDownloadButton = true,
  style,
  containerStyle,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("common.charts.intent_title");
  const effectiveDownloadText =
    downloadButtonText || t("common.charts.intent_btn_more");
  // 使用自定义Hook管理beta状态
  const { isBeta } = useBetaStatus();

  // 使用动态数据或静态数据
  const dynamicData =
    dataSource ?? (questionParams ? useQuestionList(questionParams) : null);
  const finalQuestions = dynamicData
    ? dynamicData.questions.map((q) => q.question)
    : questions || [];
  const loading = dynamicData?.loading || false;
  const loadingMore = dynamicData?.loadingMore || false;
  const error = dynamicData?.error;
  const hasMore = dynamicData?.hasMore || false;
  const loadMore = dynamicData?.loadMore;

  // 根据beta状态决定显示的问题数量
  const displayQuestions = isBeta ? finalQuestions.slice(0, 3) : finalQuestions;

  // 滚动容器引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<any>(null);

  // 滚动监听 - 自动加载更多（提前加载，更丝滑，带防抖）
  const handleScroll = useCallback(() => {
    // beta模式下禁用滚动加载
    if (
      isBeta ||
      !scrollContainerRef.current ||
      !loadMore ||
      loadingMore ||
      !hasMore
    )
      return;

    // 清除之前的定时器
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // 防抖处理
    scrollTimeoutRef.current = setTimeout(() => {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current!;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 距离底部50px时触发，更精确

      if (isNearBottom) {
        console.log("📜 滚动到底部，自动加载更多", {
          scrollTop,
          scrollHeight,
          clientHeight,
          isNearBottom,
          hasMore,
          loadingMore,
        });
        loadMore();
      }
    }, 50); // 减少防抖时间到50ms，响应更快
  }, [isBeta, loadMore, loadingMore, hasMore]);

  // 添加滚动监听
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
        // 清理定时器
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  // 调试信息
  // console.log('📊 UserIntentTable 数据状态:', {
  //   hasQuestionParams: !!questionParams,
  //   questionParams,
  //   hasDynamicData: !!dynamicData,
  //   questionsCount: finalQuestions.length,
  //   loading,
  //   loadingMore,
  //   hasMore,
  //   error
  // })

  return (
    <div>
      {/* 标题 */}
      <div style={{ marginBottom: 16 }}>
        <span
          style={{
            whiteSpace: "nowrap",
            color: "rgb(148, 163, 184)",
            fontSize: 16,
          }}
        >
          {t("common.charts.intent_subtitle")}
        </span>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          overflow: "hidden",
          ...containerStyle,
        }}
      >
        {/* 表格头部 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 10px",
            background: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: "normal",
              color: "#1f2937",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            {effectiveTitle}
          </div>
          {showDownloadButton && isBeta && (
            <button
              type="button"
              style={{
                padding: "8px 14px",
                background: "#21C4B8",
                color: "#ffffff",
                border: "0",
                borderRadius: 20,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 12px rgba(15,108,117,0.20)",
              }}
              onClick={onDownload}
            >
              {effectiveDownloadText}
            </button>
          )}
        </div>

        {/* 表格内容 - 可滚动容器 */}
        <div
          ref={scrollContainerRef}
          style={{
            maxHeight: isBeta ? "auto" : "380px", // beta模式下不限制高度，禁用滚动
            overflowY: isBeta ? "hidden" : "auto", // beta模式下禁用滚动
            overflowX: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#ffffff",
              tableLayout: "fixed", // 防止长文本挤压布局
              ...style,
            }}
          >
            <tbody>
              {error ? (
                <tr>
                  <td
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#ef4444",
                      fontSize: "14px",
                    }}
                  >
                    加载失败: {error}
                  </td>
                </tr>
              ) : displayQuestions.length === 0 && !dynamicData ? (
                <tr>
                  <td
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    {t("common.charts.intent_no_data")}
                  </td>
                </tr>
              ) : (
                displayQuestions.map((question, index) => {
                  // 获取完整的问题对象
                  let questionItem = null;
                  if (dynamicData && dynamicData.questions) {
                    questionItem = dynamicData.questions.find(
                      (q) => q.question === question
                    );
                  }

                  return (
                    <tr
                      key={index}
                      style={{
                        background: "#ffffff",
                        cursor: onQuestionClick ? "pointer" : "default",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (onQuestionClick) {
                          e.currentTarget.style.backgroundColor = "#f8fafc";
                          e.currentTarget.style.transform = "translateX(2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onQuestionClick) {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "translateX(0)";
                        }
                      }}
                      onClick={() => {
                        // 传递完整的问题对象
                        if (questionItem && onQuestionClick && dynamicData) {
                          // 传递问题对象和在完整列表中的索引
                          onQuestionClick(
                            question,
                            dynamicData.questions.indexOf(questionItem)
                          );
                        } else {
                          // 回退到原来的实现
                          onQuestionClick?.(question, index);
                        }
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 10px",
                          borderBottom: "1px solid #e5e7eb",
                          color: "#1f2937",
                          fontSize: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical" as const,
                              lineHeight: 1.6,
                              wordBreak: "break-word",
                            }}
                          >
                            {question}
                          </span>
                          {onQuestionClick && (
                            <span
                              style={{
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                                fontSize: "14px",
                                opacity: 0.8,
                                paddingLeft: 8,
                                color: "#21C4B8",
                              }}
                            >
                              {t("common.charts.intent_action_view")}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserIntentTable;
